/**
 * rb-quick-add-sheet.js
 * Mobile quick-add bottom sheet.
 *
 * How it fits the theme:
 * - product-grid-item.liquid already renders, per tile:
 *     .js-quick-add-overlay        (data-color-position / data-size-position /
 *                                   data-current-color, kept fresh by
 *                                   quick-add-overlay.js on swatch change)
 *     script.js-quick-add-variants (compact map: [id, color, size, available, finalSale])
 *     form.js-product-form         (hidden .js-quick-add-id + hidden submit
 *                                   .js-product-add that ajaxCart hooks)
 *     button.js-quick-add-mobile-trigger
 * - On mobile (<768px) we intercept the trigger in the CAPTURE phase, so
 *   quick-add-overlay.js never opens the in-tile pill overlay. Desktop is
 *   untouched.
 * - Add-to-cart reuses the tile's own form via requestSubmit(), so ajaxCart
 *   (drawer, counts, item-added animation) and GA4 events work unchanged.
 * - Sold-out rows flip the sheet to a Klaviyo back-in-stock email capture —
 *   same client API + settings as pdp-back-in-stock.js.
 */
(function () {
  'use strict';

  var MOBILE_BRKP = 768; // matches theme.mobileBrkp / quick-shop-mobile-fix.js

  var root, listEl, notifyEl, labelEl, titleEl, priceEl, imgEl;
  var config = {};
  var current = null; // { card, overlay, form, rows, notifyRow }

  function isMobile() {
    return window.innerWidth < MOBILE_BRKP;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------------------------------------------------------------- sizes */

  var LETTER_RANK = {
    xs: 0, s: 1, m: 2, l: 3, xl: 4, xxl: 5, xxxl: 6,
    'x small': 0, small: 1, medium: 2, large: 3,
    'x large': 4, 'xx large': 5, 'xxx large': 6
  };

  function normLetter(v) {
    return String(v).trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  }

  // NNxNN waist/length — used only for SORTING, never for display or lookup.
  // Range guard keeps "16x20" posters and the like out.
  function parseWxL(v) {
    var m = String(v).trim().toLowerCase().match(/^(\d{2})\s*x\s*(\d{2})$/);
    if (!m) return null;
    var w = parseInt(m[1], 10), l = parseInt(m[2], 10);
    if (w < 26 || w > 50 || l < 28 || l > 40) return null;
    return { w: w, l: l };
  }

  /**
   * Sort size rows for display. Catalog values arrive in creation order
   * (e.g. "31,32,33,34,36,29,30" or Small…XX-Large followed by X-Small).
   * - all waist×length  -> waist asc, then length asc
   * - all numeric       -> numeric asc (handles "9.5", strips leading 0s)
   * - all letter sizes  -> XS→XXXL
   * - anything else     -> original order (stable)
   */
  function sortRows(rows) {
    var n = rows.length;
    if (n < 2) return rows;

    var allWxL = rows.every(function (r) { return parseWxL(r.size); });
    if (allWxL) {
      return rows.slice().sort(function (a, b) {
        var pa = parseWxL(a.size), pb = parseWxL(b.size);
        return pa.w - pb.w || pa.l - pb.l;
      });
    }

    var allNum = rows.every(function (r) { return /^\s*0*\d+(\.\d+)?\s*$/.test(r.size); });
    if (allNum) {
      return rows.slice().sort(function (a, b) {
        return parseFloat(a.size) - parseFloat(b.size);
      });
    }

    var allLetter = rows.every(function (r) { return LETTER_RANK[normLetter(r.size)] != null; });
    if (allLetter) {
      return rows.slice().sort(function (a, b) {
        return LETTER_RANK[normLetter(a.size)] - LETTER_RANK[normLetter(b.size)];
      });
    }

    return rows;
  }

  /* ------------------------------------------------------------ tile data */

  function readVariantMap(overlay) {
    var node = overlay.querySelector('.js-quick-add-variants');
    if (!node) return [];
    var raw;
    try { raw = JSON.parse(node.textContent); } catch (e) { return []; }
    if (!Array.isArray(raw)) return [];
    return raw.map(function (row) {
      return {
        id: row[0],
        color: row[1] || '',
        size: row[2] || '',
        available: !!row[3],
        finalSale: !!row[4],
        cap: (row.length > 5 ? row[5] : null),
        price: (row.length > 6 ? row[6] : "")
      };
    });
  }

  function buildRows(overlay) {
    var variants = readVariantMap(overlay);
    var colorPos = parseInt(overlay.dataset.colorPosition, 10);
    var sizePos = parseInt(overlay.dataset.sizePosition, 10);
    var color = overlay.dataset.currentColor || '';

    // Filter to the tile's current color (kept fresh by quick-add-overlay.js
    // on swatch change — combined-listing children included).
    var rows = variants.filter(function (v) {
      return colorPos < 0 || v.color === color;
    });

    // No size option: single add row for the current variant.
    if (sizePos < 0) {
      rows = rows.slice(0, 1);
      rows.forEach(function (r) { r.size = ''; });
      return rows;
    }

    // Dedupe exact same size within the color (dirty data guard); keep the
    // available one if both exist.
    var seen = {};
    rows = rows.filter(function (r) {
      var k = r.size;
      if (seen[k]) {
        if (r.available && !seen[k].available) {
          seen[k].id = r.id;
          seen[k].available = true;
          seen[k].finalSale = r.finalSale;
          seen[k].cap = r.cap;
          seen[k].price = r.price;
        }
        return false;
      }
      seen[k] = r;
      return true;
    });

    return sortRows(rows);
  }

  function money(card) {
    // GA4 attrs are already on every card — cheap and reliable.
    var p = parseFloat(card.dataset.ga4Price);
    if (isNaN(p)) {
      var priceNode = card.querySelector('.price');
      return priceNode ? priceNode.textContent.trim().replace(/\s+/g, ' ') : '';
    }
    return '$' + p.toFixed(2);
  }

  /* ----------------------------------------------------------- open/close */

  function render() {
    var rows = current.rows;
    var single = rows.length === 1 && rows[0].size === '';

    labelEl.textContent = single ? 'Quick add' : 'Select size';

    listEl.innerHTML = rows.map(function (r, i) {
      var out = !r.available;
      return (
        '<button type="button" class="rb-qas__row' + (out ? ' rb-qas__row--out' : '') + '" data-i="' + i + '">' +
          '<span class="rb-qas__row-size">' + (single ? 'Add to cart' : esc(r.size)) + '</span>' +
          '<span class="rb-qas__row-tag">' + (out ? 'Sold out &mdash; notify me' : 'Add') + '</span>' +
        '</button>'
      );
    }).join('');
  }

  function open(card) {
    var overlay = card.querySelector('.js-quick-add-overlay');
    if (!overlay) return;
    var form = overlay.querySelector('form.js-product-form');
    if (!form) return;

    var rows = buildRows(overlay);
    if (!rows.length) return;

    current = { card: card, overlay: overlay, form: form, rows: rows, notifyRow: null };

    titleEl.textContent = card.dataset.ga4ItemName || '';
    priceEl.textContent = money(card);

    var img = card.querySelector('img');
    if (img && (img.currentSrc || img.src)) {
      imgEl.src = img.currentSrc || img.src;
      imgEl.alt = '';
      imgEl.hidden = false;
    } else {
      imgEl.hidden = true;
    }

    showList();
    render();
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('rb-qas-lock');
  }

  function close() {
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('rb-qas-lock');
    current = null;
  }

  // View state is driven by classes on the root (CSS uses !important), so
  // no theme stylesheet or [hidden] cascade quirk can defeat the toggle.
  function showList() {
    root.classList.remove('is-notify', 'is-notify-done', 'is-notify-err');
    // belt-and-suspenders for any [hidden] attrs left in markup
    notifyEl.removeAttribute('hidden');
    listEl.removeAttribute('hidden');
    labelEl.removeAttribute('hidden');
  }

  // Every lookup is guarded: a single missing node must never be able to
  // abort the flow before the view actually switches (see the <form> note in
  // the snippet — that exact failure shipped once already).
  function showNotify(row) {
    current.notifyRow = row;

    var sizeEl = root.querySelector('.js-rb-qas-notify-size');
    if (sizeEl) {
      sizeEl.textContent = row.size || (current.card.dataset.ga4ItemName || 'This item');
    }
    var emailEl = root.querySelector('.js-rb-qas-email');
    if (emailEl) emailEl.value = '';

    root.classList.remove('is-notify-done', 'is-notify-err');
    if (notifyEl) notifyEl.removeAttribute('hidden');

    // Switch the view FIRST — nothing below can prevent it.
    root.classList.add('is-notify');
  }

  /* ---------------------------------------------------------- add to cart */

  function addToCart(row, rowEl) {
    var form = current.form;
    var idInput = form.querySelector('.js-quick-add-id');
    if (!idInput) return;
    idInput.value = row.id;

    // Final-sale property. 2026-09-04: create/remove rather than toggle
    // `disabled` - the inputs are no longer rendered on full-price tiles,
    // because Shopify's accelerated checkout collector ignores `disabled`.
    if (window.RoosterFinalSale && window.RoosterFinalSale.ensure) {
      window.RoosterFinalSale.ensure(form, !!row.finalSale, row.cap, row.price, 'quickadd');
    } else {
      var fs = form.querySelector('.js-quick-add-final-sale');
      var fsBasis = form.querySelector('.js-quick-add-fs-basis');
      if (fs && !row.finalSale && fs.parentNode) fs.parentNode.removeChild(fs);
      if (fsBasis && !row.finalSale && fsBasis.parentNode) fsBasis.parentNode.removeChild(fsBasis);
    }

    rowEl.classList.add('rb-qas__row--busy');

    // Submit the tile's own form — ajaxCart owns everything from here
    // (drawer, counts, .js-product-add.is-added animation hook).
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();

    rowEl.classList.remove('rb-qas__row--busy');
    rowEl.classList.add('rb-qas__row--added');
    rowEl.querySelector('.rb-qas__row-tag').innerHTML = '&#10003; Added';

    setTimeout(close, 900);
  }

  /* ------------------------------------------------- Klaviyo back in stock */

  function submitNotify() {
    var emailInput = root.querySelector('.js-rb-qas-email');
    var email = (emailInput.value || '').trim();
    root.classList.remove('is-notify-err');

    if (!email || email.indexOf('@') < 1 || !current || !current.notifyRow) {
      emailInput.focus();
      return;
    }
    if (!config.klaviyo_public_key) {
      root.classList.add('is-notify-err');
      return;
    }

    var btn = root.querySelector('.js-rb-qas-notify-submit');
    btn.disabled = true;

    var key = encodeURIComponent(config.klaviyo_public_key);
    var promises = [];

    // 1) Back-in-stock subscription on the exact variant.
    promises.push(fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + key, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email: email } } }
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: '$shopify:::$default:::' + current.notifyRow.id
              }
            }
          }
        }
      })
    }));

    // 2) Newsletter list subscription (same gate as the PDP flow).
    if (config.newsletter_list_id) {
      promises.push(fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + key, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email: email } } },
              custom_source: 'Quick add back-in-stock'
            },
            relationships: {
              list: { data: { type: 'list', id: config.newsletter_list_id } }
            }
          }
        })
      }));
    }

    Promise.all(promises)
      .then(function (responses) {
        var failed = responses.some(function (r) { return !r.ok; });
        if (failed) throw new Error('klaviyo');
        root.classList.add('is-notify-done');
        setTimeout(close, 1400);
      })
      .catch(function () {
        root.classList.add('is-notify-err');
      })
      .then(function () {
        btn.disabled = false;
      });
  }

  /* --------------------------------------------------------------- events */

  function init() {
    root = document.querySelector('.js-rb-qas');
    if (!root) return;

    listEl = root.querySelector('.js-rb-qas-list');
    notifyEl = root.querySelector('.js-rb-qas-notify');
    labelEl = root.querySelector('.js-rb-qas-label');
    titleEl = root.querySelector('.js-rb-qas-title');
    priceEl = root.querySelector('.js-rb-qas-price');
    imgEl = root.querySelector('.js-rb-qas-img');

    var cfgNode = document.querySelector('.js-rb-qas-config');
    if (cfgNode) {
      try { config = JSON.parse(cfgNode.textContent); } catch (e) { config = {}; }
    }

    // Intercept the tile trigger in CAPTURE phase so quick-add-overlay.js
    // (which binds directly on the trigger) never sees the click on mobile.
    document.addEventListener('click', function (e) {
      if (!isMobile()) return;
      var trigger = e.target.closest && e.target.closest('.js-quick-add-mobile-trigger');
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      var card = trigger.closest('.js-product-card');
      if (card) open(card);
    }, true);

    // Size row taps (delegated).
    listEl.addEventListener('click', function (e) {
      var rowEl = e.target.closest('.rb-qas__row');
      if (!rowEl || !current) return;
      var row = current.rows[parseInt(rowEl.dataset.i, 10)];
      if (!row) return;
      if (row.available) addToCart(row, rowEl);
      else showNotify(row);
    });

    root.querySelectorAll('.js-rb-qas-close').forEach(function (el) {
      el.addEventListener('click', close);
    });

    var backBtn = root.querySelector('.js-rb-qas-notify-back');
    if (backBtn) backBtn.addEventListener('click', showList);

    // No <form> here on purpose (see snippet comment): wire the button and
    // the Enter key directly.
    var submitBtn = root.querySelector('.js-rb-qas-notify-submit');
    if (submitBtn) {
      submitBtn.addEventListener('click', function (e) {
        e.preventDefault();
        submitNotify();
      });
    }
    var emailInput = root.querySelector('.js-rb-qas-email');
    if (emailInput) {
      emailInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          submitNotify();
        }
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && root.classList.contains('is-open')) close();
    });

    // Rotating to desktop while open: hand back to the hover overlay.
    window.addEventListener('resize', function () {
      if (!isMobile() && root.classList.contains('is-open')) close();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
